import { useQuery } from '@apollo/client'
import { Button, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { Pagination } from '@material-ui/lab'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { GET_SITEKEYS_LIST } from '../../graphql/sitekeys'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { SiteKeys } from '../../types/sitekey'
import SitekeyRowMobileSkeleton from './SitekeyRowMobile.sekeleton'
import SitekeysFilter from './SitekeysFilter'
import SitekeysRowMobile from './SitekeysRowMobile'
import SitekeysTableMobile from './SitekeysTableMobile'
import useStyles from './styles/SitekeysList.styles'

interface SitekeysListMobileProps {
  hasAccessToAddNewSiteKey: boolean | undefined
  hasAccessToEditSiteKey: boolean | undefined
}

const SitekeysListMobile = ({
  hasAccessToAddNewSiteKey,
  hasAccessToEditSiteKey
}: SitekeysListMobileProps) => {
  const classes = useStyles()
  const [count, setcount] = useState<number>(0)
  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [filteredSiteKeys, setFilteredSiteKeys] = useState<SiteKeys[]>([])

  const { loading, data } = useQuery<{
    sitekeys: SiteKeys[]
  }>(GET_SITEKEYS_LIST, {
    notifyOnNetworkStatusChange: true,
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  useEffect(() => {
    setPage(1)
    const fullSiteKeyList = data?.sitekeys || []
    const newFilteredSiteKeys = matchSorter(fullSiteKeyList, searchInputValue, {
      keys: ['EntityType', 'EntityName.EN', 'EntityName.FA']
    })
    setFilteredSiteKeys(newFilteredSiteKeys)
    setcount(newFilteredSiteKeys.length)
  }, [searchInputValue, loading])

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Site keys List')}
        </Typography>
        {hasAccessToAddNewSiteKey && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            component={LocalizedLink}
            to="/sitekeys/create">
            {getTranslate('Add')}
          </Button>
        )}
      </div>
      <div>
        <SitekeysFilter
          searchInputValue={searchInputValue}
          setsearchInputValue={setsearchInputValue}
        />
        <SitekeysTableMobile />
        {loading
          ? Array(5)
              .fill('')
              .map((item, index) => (
                <SitekeyRowMobileSkeleton
                  key={`sitekey-skeleton${item}${index.toString()}`}
                />
              ))
          : filteredSiteKeys
              .slice((page - 1) * perPage, page * perPage)
              .map((item: any, index) => (
                <SitekeysRowMobile
                  key={index}
                  hasAccessToEditSiteKey={hasAccessToEditSiteKey}
                  sitekeyData={item}
                />
              ))}
      </div>
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          size="medium"
          page={page}
          onChange={(_, value) => setPage(value)}
        />
      </div>
    </div>
  )
}
export default SitekeysListMobile
